import React, { useCallback, useEffect, useState } from 'react'
import AdminService from "../services/AdminService";
import { Dictionary } from 'highcharts';
import { IsirRecordElementInfo, IsirEnumInfo } from '../models/Interviewypes';


interface CorrectionsDisplayPageProps {
}


export const CorrectionsDisplayPage: React.FC<CorrectionsDisplayPageProps> = () => {
    let [isirEnumInfo, setIsirEnumInfo] = useState<IsirEnumInfo[]>([]);
    let [isirRecordElementInfos, setIsirRecordElementInfos] = useState<IsirRecordElementInfo[]>([]);
    let [yearCode] = useState<string>("5");
    let [isirLines, setIsirLines] = useState<string[]>([""]);
    let [corrections, setCorrections] = useState<{[index: number] : {[attributeName: string] : string};}>({});

    let getNewCorrectionTabs = useCallback((isirElements : IsirRecordElementInfo[], oldIsirElements : IsirRecordElementInfo[]) => {
        let newIsirTabs: string[][] = [];
        let tabDict: {[index: number] : {[attributeName: string] : string};} = {};

        // Header
        for (let tabIndex: number = 0; tabIndex < isirLines.length; tabIndex++) {
            if (!tabDict[tabIndex]) tabDict[tabIndex] = {};
            let firstName = isirLines[tabIndex].substring(56, isirLines[tabIndex].indexOf(' ', 56) < 91 ? isirLines[tabIndex].indexOf(' ', 56) : 91);
            let middleName = isirLines[tabIndex].substring(91, isirLines[tabIndex].indexOf(' ', 91) < 106 ? isirLines[tabIndex].indexOf(' ', 91) : 106);
            let lastName = isirLines[tabIndex].substring(106, isirLines[tabIndex].indexOf(' ', 106) < 141 ? isirLines[tabIndex].indexOf(' ', 106) : 141);
            let suffix = isirLines[tabIndex].substring(141, isirLines[tabIndex].indexOf(' ', 141));
            let dob = isirLines[tabIndex].substring(907, isirLines[tabIndex].indexOf(' ', 907));

            tabDict[tabIndex]["firstName"] = firstName;
            tabDict[tabIndex]["middleName"] = middleName;
            tabDict[tabIndex]["lastName"] = lastName;
            tabDict[tabIndex]["studentSuffix"] = suffix;
            tabDict[tabIndex]["studentDOB"] = dob; 
        }

        // Corrections
        for (let tabIndex: number = 0; tabIndex < isirLines.length; tabIndex++) {
            for (let i: number = 0; i < 12; i++) {
                let cpsIdPosition = 151 + 63 * i;
                let cpsNumber = isirLines[tabIndex].substring(cpsIdPosition, cpsIdPosition + 3);
                if (!cpsNumber || cpsNumber.trim() === '') {
                    break;
                }
                // If it doesnt find the cps number, means its not correctable. It still gets added to the dict for now but wont be displayed
                let attributeName = cpsNumber + 'attributename';
                for (let j: number = 0; j < isirElements.length; j++) {
                    if (isirElements[j].cpsNumber !== null && isirElements[j].cpsNumber !== undefined && isirElements[j].cpsNumber.substring(4) === cpsNumber) {
                        attributeName = isirElements[j].attributeName;
                        break;
                    }
                }
                let correctionValue = isirLines[tabIndex].substring(cpsIdPosition + 3, isirLines[tabIndex].indexOf(' ', cpsIdPosition));
                tabDict[tabIndex][attributeName] = correctionValue;
            }
        }
        setCorrections(tabDict);
        return newIsirTabs;
    }, [isirLines]);

    useEffect(() => {
        let getData = async () => {
            let isirElements : IsirRecordElementInfo[] = await AdminService.getAllIsirRecordElementInfoByAidYear(yearCode);
            let enumInfos = await AdminService.getIsirEnumInfo() ?? [];
            enumInfos = enumInfos.filter(x => x.aidYear.toString().endsWith(yearCode));
            enumInfos = enumInfos.filter((enumInfo : IsirEnumInfo, index : number) => 
                enumInfos.findIndex(x => x.code === enumInfo.code && x.value === enumInfo.value) === index);
            enumInfos = enumInfos.sort((a, b) => a.code === b.code ? a.value.localeCompare(b.value) : a.code.localeCompare(b.value));

            // Define a type for the grouped object
            type GroupedCodeObject = { [code: string]: IsirEnumInfo[] };

            // Group elements by code
            const groupedByCode: GroupedCodeObject = enumInfos.reduce((acc, current) => {
                if (!acc[current.code]) {
                    acc[current.code] = [];
                }
                acc[current.code].push(current);
                return acc;
            }, {} as GroupedCodeObject);

            // Add element with value blank if none of them have value set to blank
            for (const code in groupedByCode) {
                const elements = groupedByCode[code];
                var lengthOfValue = elements[0].value.length;
                var str = new Array(lengthOfValue + 1).join( ' ' );

                if (!elements.some(element => element.value.trim() === '')) {
                    elements.push({
                        id: 9999,
                        code: code,
                        name: 'Blank on ISIR',
                        value: str,
                        description: ' ',
                        suppressed: false,
                        isirRecordInfoID: 9999,
                        aidYear: 9999,
                    });
                }
            }

            // Resulting array with added elements if needed
            const resultArray: IsirEnumInfo[] = Object.values(groupedByCode).flat();

            setIsirEnumInfo(resultArray);

            isirElements = isirElements.sort((a, b) => a.startOffset - b.startOffset);
            
            let oldIsirElements : IsirRecordElementInfo[] = [];

            setIsirRecordElementInfos((oldVal : IsirRecordElementInfo[]) => {
                oldIsirElements = oldVal ?? [];
                return isirElements;
            });
            
            getNewCorrectionTabs(isirElements, oldIsirElements);
            
            openTab(0);
        }
        getData();
    }, [yearCode, getNewCorrectionTabs])

    function loadTemplateFile(files : FileList | null) {
        if(!files || files.length < 1) 
            return;
        let reader = new FileReader();
        reader.onloadend = (ev : ProgressEvent<FileReader>) => {
            let text : string = ev.target?.result as string;
            let lines : string[] = text.split("\n");
            setIsirLines(lines.filter((line : string, index : number) => 
                index > 0 && index < lines.length - 1));
        };
        reader.readAsText(files[0]);
    }

    function openTab(tabIndex: number) {
        let tabContent = document.getElementsByClassName("correctionsDisplayTabContent") as HTMLCollectionOf<HTMLElement>;
        for (let i = 0; i < tabContent.length; i++) 
            tabContent[i].style.display = "none";

        let tabLinks = document.getElementsByClassName("tabLinks");
        for (let i = 0; i < tabLinks.length; i++) 
            tabLinks[i].className = tabLinks[i].className.replace(" active", "");

        let element = document.getElementById("isir-tab-" + tabIndex);
        if (element)
            element.style.display = "inline-block";
        tabLinks[tabIndex].className += " active";
    }

    let tabs = Object.values(corrections);

    return (
        <div>
            <br />
            <h1>Corrections Display</h1>
            <br />
            <div>
                Template File&nbsp;
                <input type="file" onChange={(event : React.ChangeEvent<HTMLInputElement>) => 
                    loadTemplateFile(event.target.files)}/>
                <br /><br />
            </div>

            <div className="correctionsDisplayTab">
                {tabs.map((_, index : number) => {
                    return <button className="tabLinks" key={index}
                        onClick={() => openTab(index)}>
                            Corrections Line {index + 1}</button>;
                })}
            </div>

            {tabs.map((correction: Dictionary<string>, tabIndex: number) => {
                return <div id={"isir-tab-" + tabIndex} className="correctionsDisplayTabContent" key={tabIndex}>
                    {isirRecordElementInfos.map((element: IsirRecordElementInfo) => {
                    if (Object.keys(correction).includes(element.attributeName)) {
                        return <div className="correctionsDisplayColumn-3" key={tabIndex + element.attributeName}>
                            {element.displayName}
                            <br />
                            {!element.attributeName.startsWith("commentCode") && element.enumName ? <select value={correction[element.attributeName]}>
                                {isirEnumInfo.filter(x => x.code === element.enumName).map((enumInfo: IsirEnumInfo) => {
                                    return <option key={element.name + "-enum-" + enumInfo.code + "-" + enumInfo.value}
                                        value={enumInfo.value}>{enumInfo.value} - {enumInfo.name}</option>
                                })}
                            </select> : 
                            <input value={correction[element.attributeName]}/>}
                            
                        </div>;
                    } else return null})}
                </div>
            })}

            <div>
            </div>
            <div>
                <br /><br />
            </div>
        </div>
    );
}